import { useNavigate } from "react-router-dom";
import { ADMIN_COLORS as C } from "../monitoring/adminColors";
import Icon from "components/common/Icon";
import { useOviziaAuditLogs } from "hooks/ovizia/useOviziaAuditLogs";
import {
  AUDIT_ACTION_TYPES,
  AUDIT_ENTITY_TYPES,
} from "hooks/ovizia/auditLogApi";

const TONE_STYLE = {
  positive: { bg: C.greenBg, fg: C.green },
  negative: { bg: C.redBg, fg: C.red },
  warning: { bg: "rgba(217,119,6,0.12)", fg: C.amber },
  neutral: { bg: C.blueBg, fg: C.blue },
};

// Groups the action dropdown into <optgroup>s so 14 actions stay scannable.
const ACTION_GROUPS = [
  {
    label: "Website",
    values: [
      "WEBSITE_CREATED",
      "CONTENT_UPDATED",
      "GENERATION_TRIGGERED",
      "GENERATION_RETRIED",
      "REGENERATED_FROM_BLUEPRINT",
      "PUBLISHED",
    ],
  },
  {
    label: "Newsletter",
    values: [
      "NEWSLETTER_CAMPAIGN_CREATED",
      "NEWSLETTER_CAMPAIGN_UPDATED",
      "NEWSLETTER_CAMPAIGN_SENT",
      "NEWSLETTER_CAMPAIGN_DELETED",
    ],
  },
  {
    label: "Domain payments",
    values: [
      "DOMAIN_PAYMENT_CHARGED",
      "DOMAIN_PAYMENT_AUTHORIZED",
      "DOMAIN_PAYMENT_RELEASED",
      "DOMAIN_PAYMENT_FAILED",
    ],
  },
];

const ACTION_LABEL = Object.fromEntries(
  AUDIT_ACTION_TYPES.map((a) => [a.value, a.label]),
);

function Pill({ tone, children }) {
  const style = TONE_STYLE[tone] || TONE_STYLE.neutral;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        background: style.bg,
        color: style.fg,
        fontSize: 11,
        fontWeight: 700,
        padding: "3px 9px",
        borderRadius: 999,
        width: "max-content",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function IdChip({ label, value, onOpen }) {
  if (!value) return null;
  const short = value.length > 8 ? value.slice(0, 8) : value;
  const content = (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        fontSize: 11,
        fontFamily: "monospace",
        fontWeight: 600,
        color: C.textMuted,
        background: C.bgSurface,
        border: `1px solid ${C.border}`,
        borderRadius: 6,
        padding: "3px 8px",
      }}
      title={value}
    >
      {label}: {short}
    </span>
  );
  if (!onOpen) return content;
  return (
    <button
      onClick={onOpen}
      style={{
        border: "none",
        background: "none",
        padding: 0,
        cursor: "pointer",
      }}
    >
      {content}
    </button>
  );
}

function ChangedFieldsTags({ fields }) {
  if (!fields || fields.length === 0) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
      {fields.map((f) => (
        <span
          key={f}
          style={{
            fontSize: 10,
            fontFamily: "monospace",
            color: C.textFaint,
            background: C.bgSurface,
            border: `1px solid ${C.border}`,
            borderRadius: 4,
            padding: "1px 6px",
          }}
        >
          {f}
        </span>
      ))}
    </div>
  );
}

function AuditLogRow({ entry, onOpenEntity }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 14,
        padding: "14px 16px",
        borderTop: `1px solid ${C.border}`,
        alignItems: "flex-start",
      }}
    >
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: "50%",
          background: (TONE_STYLE[entry.tone] || TONE_STYLE.neutral).bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon
          name={entry.icon}
          size={14}
          style={{ color: (TONE_STYLE[entry.tone] || TONE_STYLE.neutral).fg }}
        />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <Pill tone={entry.tone}>{entry.label}</Pill>
          <span style={{ fontSize: 11, color: C.textFaint }}>
            {new Date(entry.createdAt).toLocaleString()}
          </span>
        </div>

        <div style={{ fontSize: 13, color: C.text, marginTop: 4 }}>
          {entry.summary}
        </div>
        <ChangedFieldsTags fields={entry.changedFields} />

        <div
          style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}
        >
          <IdChip
            label={entry.entityType === "WEBSITE" ? "Website" : "Order"}
            value={entry.entityId}
            onOpen={() =>
              onOpenEntity(`/ovizia/websites?websiteId=${entry.entityId}`)
            }
          />
          <IdChip
            label="Actor"
            value={entry.actorId}
            onOpen={
              entry.actorId
                ? () => onOpenEntity(`/olitask/users/${entry.actorId}`)
                : undefined
            }
          />

          <IdChip label="Ref" value={entry.correlationId} />
        </div>
      </div>
    </div>
  );
}

export default function AuditLogPage() {
  const navigate = useNavigate();
  const {
    entries,
    loading,
    query,
    setQuery,
    entityType,
    setEntityType,
    action,
    setAction,
    refresh,
  } = useOviziaAuditLogs();

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        padding: "24px 28px",
        fontFamily: "Inter, system-ui, sans-serif",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          marginBottom: 20,
          flexShrink: 0,
        }}
      >
        <div>
          <h1
            style={{ fontSize: 20, fontWeight: 800, color: C.green, margin: 0 }}
          >
            Audit log
          </h1>
          <p style={{ fontSize: 13, color: C.textMuted, margin: "4px 0 0" }}>
            Every action a user or admin took, and what changed.
          </p>
        </div>

        <button
          onClick={refresh}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 12,
            fontWeight: 600,
            color: C.textMuted,
            border: `1px solid ${C.border}`,
            borderRadius: 7,
            background: "#fff",
            padding: "7px 12px",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <Icon name="refresh" size={13} /> Refresh
        </button>
      </div>

      <div
        style={{
          display: "flex",
          gap: 10,
          marginBottom: 16,
          flexWrap: "wrap",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            flex: 1,
            minWidth: 220,
            display: "flex",
            alignItems: "center",
            gap: 8,
            border: `1px solid ${C.border}`,
            borderRadius: 8,
            padding: "9px 12px",
            background: "#fff",
          }}
        >
          <Icon name="search" size={15} style={{ color: C.textFaint }} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by summary, entity ID, actor ID, or reference..."
            style={{
              border: "none",
              outline: "none",
              flex: 1,
              fontSize: 13,
              fontFamily: "inherit",
            }}
          />
        </div>

        <select
          value={entityType}
          onChange={(e) => setEntityType(e.target.value)}
          style={{
            fontSize: 12,
            fontWeight: 600,
            padding: "6px 8px",
            borderRadius: 6,
            border: `1px solid ${C.border}`,
            background: "#fff",
            color: C.text,
            cursor: "pointer",
          }}
        >
          <option value="">All entity types</option>
          {AUDIT_ENTITY_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>

        <select
          value={action}
          onChange={(e) => setAction(e.target.value)}
          style={{
            fontSize: 12,
            fontWeight: 600,
            padding: "6px 8px",
            borderRadius: 6,
            border: `1px solid ${C.border}`,
            background: "#fff",
            color: C.text,
            cursor: "pointer",
          }}
        >
          <option value="">All actions</option>
          {ACTION_GROUPS.map((group) => (
            <optgroup key={group.label} label={group.label}>
              {group.values.map((v) => (
                <option key={v} value={v}>
                  {ACTION_LABEL[v]}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      <div
        style={{
          border: `1px solid ${C.border}`,
          borderRadius: 10,
          background: "#fff",
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
        }}
      >
        {loading ? (
          <div
            style={{
              padding: 40,
              textAlign: "center",
              color: C.textFaint,
              fontSize: 13,
            }}
          >
            Loading audit log…
          </div>
        ) : entries.length === 0 ? (
          <div
            style={{
              padding: 40,
              textAlign: "center",
              color: C.textFaint,
              fontSize: 13,
            }}
          >
            No entries match these filters.
          </div>
        ) : (
          entries.map((entry) => (
            <AuditLogRow
              key={entry.id}
              entry={entry}
              onOpenEntity={(path) => navigate(path)}
            />
          ))
        )}
      </div>
    </div>
  );
}
